import { createAsyncThunk, unwrapResult } from '@reduxjs/toolkit'
import { useMediaQuery } from 'react-responsive'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { RootState } from '../../app/store'
import {
    getContacts,
    getCustomerShipTos,
    getRequirements,
    isEmail,
    LOADING_STATES,
    openCustomerTypeModal,
    openIdentifierUpdate,
    openUniqueCustomerNameModal,
    PACustomerContactViewRow,
    selectCustomer,
    selectFilteredAccounts,
} from '../../features/customer/customerSlice'
import { startQuote } from '../../features/order/orderSlice'

import { GenericContainer } from '../../GlobalComponents/GenericContainer/GenericContainer'
import { logEvent } from '../../GlobalComponents/GlobalFunctions'
import CustomerAccountCard from './CustomerAccountCard'
import styles from './CustomerAccountSelect.module.scss'

const CustomerAccountSelect = (props: any) => {
    let customers = useAppSelector(selectFilteredAccounts)
    let dispatch = useAppDispatch()
    let customersLoading = useAppSelector((state) => state.customer.customersLoading)
    let currentFilter = useAppSelector((state) => state.customer.currentFilter)
    let currentSearch = useAppSelector((state) => state.customer.currentSearch)
    let unfilteredCustomers = useAppSelector((state) => state.customer.CustomerAccounts)
    let loadingInfo = useAppSelector((state) => state.customer.loadingInfo)
    const hasExactMatch = customers.filter((row) => row.ExactMatch).length > 0

    const isLargeView = useMediaQuery({
        query: '(min-width: 1484px)',
    })

    //TODO move to appropriate spot
    // let myThunk =
    //     (account: PACustomerContactViewRow) =>
    //     async (dispatch: ThunkDispatch<RootState, null, AnyAction>) => {
    //         let promShipTos = dispatch(getCustomerShipTos(account.AccountCode))
    //         let promContacts = dispatch(getContacts(account.AccountCode))
    //         dispatch(getRequirements(account.AccountCode))
    //         let payload = unwrapResult(await promShipTos)
    //         dispatch(selectCustomer(account))
    //         let defaultShipTo = payload.find(
    //             (row) => row.ShipToAddress1 === account?.ShipToAddress1
    //         )
    //         let contactPayload = unwrapResult(await promContacts)
    //         let contact = contactPayload.data.find(
    //             (row) =>
    //                 row.EmailAddress &&
    //                 row.EmailAddress!.toUpperCase() === currentSearch.toUpperCase().trim()
    //         )

    //         dispatch(startQuote({ account: account!, shipTo: defaultShipTo!, contact }))
    //     }
    let startQuoteExternal = createAsyncThunk<any, any, { state: RootState }>(
        'order/startQuoteExternal',
        async (account: PACustomerContactViewRow, thunkAPI) => {
            let promShipTos = dispatch(getCustomerShipTos(account.AccountCode))
            let promContacts = dispatch(getContacts(account.AccountCode))
            thunkAPI.dispatch(getRequirements(account.AccountCode))
            let shipToResult = unwrapResult(await promShipTos)
            thunkAPI.dispatch(selectCustomer(account))
            let defaultShipTo = shipToResult.find(
                (row) =>
                    row.ShipToAddress1 === account?.ShipToAddress1 &&
                    row.ShipToAddress2 === account.ShipToAddress2 &&
                    row.ShipToName === account.ShipToName &&
                    row.ShipToCity === account.ShipToCity &&
                    row.ShipToState === account.ShipToState &&
                    row.ShipToCountry === account.ShipToCountry &&
                    row.ShipToZipcode === account.ShipToZipcode
            )
            let contactResult = unwrapResult(await promContacts)
            let contact = contactResult.data.find(
                (row) =>
                    row.EmailAddress &&
                    row.EmailAddress!.toUpperCase() === currentSearch.toUpperCase().trim()
            )

            thunkAPI.dispatch(
                startQuote({
                    account: account!,
                    shipTo: defaultShipTo!,
                    contact,
                })
            )
        }
    )
    const getCurrentResultText = (numResults: string) => {
        return (
            <>
                {!hasExactMatch && isEmail(currentSearch) && (
                    <div
                        style={{ marginTop: '-1.2em', color: 'var(--fadedText)' }}
                        data-testid={'noResultsText'}
                    >
                        No results found for{' '}
                        <span
                            style={{ fontWeight: 500, color: 'black' }}
                        >{`"${currentSearch}"`}</span>
                    </div>
                )}
                <div
                    data-testid={'noResultsText'}
                    style={{ marginTop: 10, color: 'var(--fadedText)' }}
                >
                    {`${numResults ? numResults : 'No'} result${
                        numResults !== '1' ? 's' : ''
                    } found for `}
                    <span style={{ fontWeight: 500, color: 'black' }}>{`"${
                        !hasExactMatch && isEmail(currentSearch)
                            ? currentSearch.split('@')[1]
                            : currentSearch
                    }"`}</span>
                </div>
            </>
        )
    }

    const onCustomerSelect = (row: PACustomerContactViewRow) => {
        if (props.onCustomerSelect) return props.onCustomerSelect(row)

        dispatch(startQuoteExternal(row)) // default for FastQuote
    }

    if (customersLoading === LOADING_STATES.INIT) return null
    return customersLoading === LOADING_STATES.LOADING ||
        (customers.length > 0 && !customers[0].OrderNumberExactMatch) ? (
        <GenericContainer
            width={isLargeView ? 399 : 369}
            className={styles.accountSearchContainer}
            topLevelClassname={styles.topContainer}
            resetKey={currentSearch + currentFilter}
            pagerPosition={'BOTTOM'}
            spacingBetweenCards={isLargeView ? 40 : 10}
            loading={customersLoading === LOADING_STATES.LOADING}
            // infoText={loaded ? getInfoText(searchValue, hasExactMatch, isEmailSearch) : ''}
            resultTextFunc={(numResults: string) =>
                currentSearch && getCurrentResultText(numResults)
            }
        >
            {customers.map((row, i) => (
                <div
                    className={styles.cardContainer}
                    key={row.AccountCode}
                    style={{ marginRight: isLargeView ? 40 : 10 }}
                >
                    <CustomerAccountCard
                        customer={row}
                        loadingInfo={loadingInfo[row.AccountCode] || {}}
                        onSelect={() => onCustomerSelect(row)}
                        openIdentifierUpdate={() => {
                            logEvent({
                                eventName: 'Identifier Edit Acct Search',
                                string2: row.AccountCode,
                            })
                            dispatch(openIdentifierUpdate(row))
                        }}
                        openCustomerTypeModal={() => {
                            logEvent({
                                eventName: 'Customer Type Edit Acct Search',
                                string2: row.AccountCode,
                            })
                            dispatch(openCustomerTypeModal(row))
                        }}
                        openUniqueCustomerNameModal={() => {
                            logEvent({
                                eventName: 'Unique Cust Edit Acct Search',
                                string2: row.AccountCode,
                            })
                            dispatch(openUniqueCustomerNameModal(row))
                        }}
                    />
                </div>
            ))}
        </GenericContainer>
    ) : (
        <div
            style={{
                marginTop: unfilteredCustomers.length > 3 ? 40 : 10,
                marginBottom: 10,
                color: 'var(--fadedText)',
            }}
        >
            {' '}
            {(unfilteredCustomers.length === 0 || unfilteredCustomers[0].OrderNumberExactMatch) &&
            currentSearch ? (
                <span data-testid='noResultsText'>{`Displaying 0 results for "${currentSearch}"`}</span>
            ) : (
                ''
            )}{' '}
        </div>
    )
}

export default CustomerAccountSelect
